import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import PreAuthHeader from '../login/components/PreAuthHeader';
import BackgroundOverlay from '../login/components/BackgroundOverlay';
import authService from '../../utils/authService';

const Signup = () => {
  const navigate = useNavigate();
  const { signUp, user, authError } = useAuth();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    role: 'agent',
    purchase_code: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/contract-upload');
    }
  }, [user, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Purchase code is now optional for testing
    // if (formData.role === 'agent' && !formData.purchase_code.trim()) {
    //   newErrors.purchase_code = 'Purchase code is required for agent accounts';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const verifyPurchaseCode = async (code) => {
    if (!code.trim()) return false;

    try {
      const result = await authService.verifyAccessCode(code);
      return result.success;
    } catch (error) {
      console.log('Purchase code verification error:', error);
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Auto-verify purchase code for agents
    if (name === 'purchase_code' && formData.role === 'agent' && value.trim()) {
      verifyPurchaseCode(value).then(isValid => {
        setCodeVerified(isValid);
        if (!isValid) {
          setErrors(prev => ({
            ...prev,
            purchase_code: 'Invalid or expired purchase code'
          }));
        }
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Only verify purchase code if one is provided
    if (formData.role === 'agent' && formData.purchase_code.trim()) {
      const isValidCode = await verifyPurchaseCode(formData.purchase_code);
      if (!isValidCode) {
        setErrors(prev => ({
          ...prev,
          purchase_code: 'Invalid or expired purchase code'
        }));
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const result = await signUp(formData.email, formData.password, {
        full_name: formData.full_name,
        company_name: formData.company_name,
        role: formData.role,
        purchase_code: formData.purchase_code
      });

      if (result.success) {
        navigate('/contract-upload');
      }
    } catch (error) {
      console.log('Signup error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleOptions = [
    { value: 'agent', label: 'Real Estate Agent' },
    { value: 'developer', label: 'Platform Developer' },
    { value: 'admin', label: 'System Administrator' }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <BackgroundOverlay />
      <PreAuthHeader />

      <div className="relative z-content pt-16">
        <div className="min-h-screen flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className="bg-card/80 backdrop-blur-md border border-border/50 rounded-xl p-8 shadow-2xl">
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  Create Account
                </h1>
                <p className="text-muted-foreground font-body">
                  Join ContractParser Pro and streamline your workflow
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  {/* Full Name */}
                  <div>
                    <Input
                      label="Full Name"
                      name="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      error={errors.full_name}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Input
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={errors.email}
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <Input
                      label="Password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      error={errors.password}
                      placeholder="Create a password"
                      required
                    />
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <Input
                      label="Confirm Password"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      error={errors.confirmPassword}
                      placeholder="Confirm your password"
                      required
                    />
                  </div>

                  {/* Company Name */}
                  <div>
                    <Input
                      label="Company Name (Optional)"
                      name="company_name"
                      type="text"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      placeholder="Enter your company name"
                    />
                  </div>

                  {/* Role */}
                  <div>
                    <Select
                      label="Account Type"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      options={roleOptions}
                      error={errors.role}
                      required
                    />
                  </div>

                  {/* Purchase Code for Agents */}
                  {formData.role === 'agent' && (
                    <div>
                      <Input
                        label="Purchase Code (Optional)"
                        name="purchase_code"
                        type="text"
                        value={formData.purchase_code}
                        onChange={handleInputChange}
                        error={errors.purchase_code}
                        placeholder="Enter your purchase code (optional)"
                        rightIcon={codeVerified ? "CheckCircle" : null}
                        rightIconColor={codeVerified ? "var(--color-success)" : null}
                      />
                      <p className="text-sm text-muted-foreground mt-2">
                        Purchase codes are optional for testing. Leave empty to proceed without validation.
                      </p>
                    </div>
                  )}
                </div>

                {/* Error Display */}
                {authError && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                    <p className="text-destructive text-sm font-body">
                      {authError}
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="default"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating Account...' : 'Create Account'}
                </Button>

                {/* Sign In Link */}
                <div className="text-center">
                  <p className="text-muted-foreground font-body text-sm">
                    Already have an account?{' '}
                    <Link
                      to="/login"
                      className="text-primary hover:text-primary/80 font-medium transition-colors duration-200"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </form>
            </div>

            {/* Developer Note */}
            <div className="mt-8 bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                Preview Mode Active
              </h3>
              <p className="text-muted-foreground font-body text-sm">
                This is a development preview. All features are accessible for testing purposes.
                In production, proper role-based access controls will be enforced.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;